/* eslint-disable max-len */
import { BehaviorSubject } from 'rxjs';
import { union, cloneDeep } from 'lodash';
import moment from 'moment-timezone';
import * as uuid from 'uuid';

import { ReportSeriesApiModel } from '../../../api/reports/model/report-series-api.model';
import { SearchCriteriaModel } from '../../../api/mentions/model/search-criteria.model';
import { DateFacetModel } from '../../../api/mentions/model/date-facet.model';
import { DateFacetTypesEnum, dateTypeToDateFacetMap } from '../../../api/mentions/enum/date-facet-types.enum';
import { AggregateDateFacetTypesEnum } from '../../../api/mentions/enum/aggregate-date-facet-types.enum';
import { ProminenceGroupApiModel } from '../../../api/monitoring/model/prominence-group-api.model';
import { ProminenceByReachRequestModel } from '../../../api/mentions/model/prominence-by-reach-request.model';
import { DashboardSeriesApiModel } from '../../../api/dashboard/models/dashboard-series-api.model';
import { DateQueryTypeEnum } from '../../../api/mentions/enum/date-query-type.enum';
import { SearchCriteriaQuerySentimentsEnum } from '../../../api/mentions/enum/search-criteria-query-sentiments.enum';
import { SharedReportSeriesApiModel } from '../../../api/reports/model/shared-report-series-api.model';
import { DataConnectorTypesEnum } from '../../../api/externalData/enums/data-connector-types.enum';
import { GoogleAnalyticsReportRequestTypeEnum } from '../../../api/externalData/enums/google-analytics-report-request-type.enum';
import { GoogleAnalyticsReportTypesEnum } from '../../../api/externalData/enums/google-analytics-report-types.enum';

import { MentionSearchModel } from '../../../Monitor/Shared/models/mention-search.model';
import { MentionSearchCriteriaModel } from '../../../Monitor/Shared/models/mention-search-criteria.model';

import { SeriesModel, SubSeriesModel } from '../../Charts/models/series.model';
import { DataPointModel } from '../../Charts/models/data-point.model';

import { DateQueryModel } from '../../Forms/models/date-query.model';
import { Mention } from '../../Mentions/models/mention';
import { JournodexGeographyModel } from '../../Journodex/models/journodex-geography.model';
import { GenericStyleModel } from '../../Styles/models/generic-style.model';
import { dateQueryInput30DaysOption } from '../../Forms/models/date-query-input-options.model';
import {
    ExternalChartMetricsEnum,
    externalChartMetricToTopReferralSourcesReportRequestTypeMap,
    externalChartMetricToTopSocialReferrersReportRequestTypeMap
} from '../enums/external-chart-metrics.enum';
import { externalDataSeriesTypes, mentionSeriesTypes, SeriesSourceTypesEnum, seriesSourceTypesToSeriesTypesMap, SeriesTypeEnum, seriesTypesToSeriesSourceTypesMap, socialSeriesTypes } from '../enums/series-type.enum';
import { ChartMetricsEnum, chartMetricsToNeoFacetTermFieldMap } from '../enums/chart-metrics.enum';
import { reportChartInfoMap, ReportBlockTypeEnum } from '../enums/report-block-type.enum';
import { PREventModel } from './pr-event.model';
import { ChartDisplayElementModel } from './chart-display-element.model';
import { ReportSeriesMentionsModel } from './report-series-mentions.model';
import { ReportDateRangeModel } from './report-date-range.model';
import { TermTypesEnum, termTypesToNeoFacetPrefixMap } from '../enums/term-types.enum';
import { NeoFacetSortByEnum } from '../../../api/mentions/enum/neo-facet-sort-by.enum';
import { SortOrderEnum } from '../../../api/shared/enums/sort-order.enum';
import { NeoFacetTermFieldApiEnum } from '../../../api/mentions/enum/neo-facet-term-field-api.enum';
import { BucketFunctionApiEnum } from '../../../api/mentions/enum/bucket-function-api.enum';
import { EngagementAgeApiEnum } from '../../../api/mentions/enum/engagement-age-api.enum';
import { NeoFacetRequestApiModel } from '../../../api/mentions/model/neo-facet-request-api.model';
import { DateQueryDateTypeEnum } from '../../../api/mentions/enum/date-query-date-type.enum';
import { NeoFacetDateFieldApiEnum } from '../../../api/mentions/model/neo-facet-date-field-api.enum';
import { SearchCriteriaSortFieldEnum } from '../../../api/mentions/enum/search-criteria-sort-field.enum';
import { MediaTypeEnum } from '../../Basic/enums/media-type.enum';
import { ChartSpecificTypesEnum } from '../enums/chart-specific-types.enum';
import { AggregateFunctionApiEnum } from '../../../api/mentions/enum/aggregate-function-api.enum';
import { AggregateFieldsApiEnum } from '../../../api/mentions/enum/aggregate-fields-api.enum';
import { RelativeTimezoneDateTypeEnum } from '../../../api/mentions/enum/relative-timezone-date-type.enum';
import { SortableMentionBlockTypeEnum } from '../enums/sortable-mention-block-type.enum';
import { AgentRouteSectionEnum, agentSectionRoute } from '../../../AutomationAgents/AutomationAgentsRouter/automation-agents-route.const';
import { GeographyTypesEnum } from '../../../api/journodex/enum/geography-types.enum';
import { ReportsNeoFacetKeys } from '../enums/reports-neo-facet-keys.enum';
import { socialMetricsAggregateBucketRequests, engagementsNeoFacet } from './report-neo-facet-requests.model';
import { ReportSeriesSearchResult } from './report-series-search-result';
import { topContactsNeoFacetRequest, topOutletsNeoFacetRequest } from './report-neo-facet-requests.model';
import { ChartTimeIntervalsEnum } from '../enums/chart-time-intervals.enum';
import { EngagementBucketRequestApiModel } from '../../../api/mentions/model/engagement-bucket-request-api.model';


export class ReportSeriesModel implements SeriesModel {
    id: number;
    label: string;
    color: string;
    dateRange: ReportDateRangeModel;
    type: SeriesTypeEnum;

    mentions: ReportSeriesMentionsModel;
    fullSearch: MentionSearchCriteriaModel;

    externalData?: {
        dataConnectorId: number;
        externalDataId: string;
        externalDataType: DataConnectorTypesEnum;
        timeZone?: string;
        googleAnalyticsReportType?: GoogleAnalyticsReportRequestTypeEnum;

        googleAnalyticsAccountId?: string;
        googleAnalyticsPropertyId?: string;
    };
    valueAxisKey?: string;
    subSeries?: SubSeriesModel[];

    metric: ChartMetricsEnum;
    metrics: ChartMetricsEnum[];

    dataProvider: BehaviorSubject<DataPointModel[]>;
    previousPeriodDataProvider: BehaviorSubject<DataPointModel[]>;
    mentionProvider?: BehaviorSubject<Mention[]>;
    hasPreviousPeriod?: boolean;
    styles: GenericStyleModel;

    sentiments: SearchCriteriaQuerySentimentsEnum[];
    referralTypes: GoogleAnalyticsReportTypesEnum[];
    prEvents: PREventModel[];
    timeInterval: ChartTimeIntervalsEnum;

    dataIndex?: number;
    key?: string;

    prominenceTrendGroup: ProminenceGroupApiModel;

    // f/e only
    isSharedReport: boolean = false;
    useMentionProvider: boolean = false;
    termType: TermTypesEnum;
    numOfItems: number = 10;
    sortField: SearchCriteriaSortFieldEnum;
    appendCombineQuery: boolean = true;

    _chartType: ReportBlockTypeEnum;
    chartSpecificType: ChartSpecificTypesEnum;

    seriesUniqueUuid: string;
    cacheResult: boolean = false;
    searchResults: ReportSeriesSearchResult = new ReportSeriesSearchResult();

    set chartType(chartType: ReportBlockTypeEnum) {
        this._chartType = chartType;
        const chartInfo = reportChartInfoMap.get(chartType);
        this.mentions.facetTypes = chartInfo.mentionFacets;
        this.mentions.aggregateDateFacets = chartInfo.aggregateDateFacets;
        // must make a clone or all neofacets that use the same act a pass by reference
        this.mentions.neoFacets = cloneDeep(chartInfo.neoFacetRequestApiModel);

        if (this.externalData && chartInfo.googleAnalyticsReportType) {
            this.externalData.googleAnalyticsReportType = chartInfo.googleAnalyticsReportType;
        }
        if (!this.dateRange) {
            this.dateRange = new ReportDateRangeModel().setFromDateQuery(dateQueryInput30DaysOption.value.query);
        }
        this.mentions.neoFacets?.forEach(facet => {
            if (facet.dateField) {
                // TODO: when folders use neoFacets, two more date types need to be added here
                if (this.dateRange.dateType === DateQueryDateTypeEnum.PUBLISHED) {
                    facet.dateField = NeoFacetDateFieldApiEnum.CREATED_AT;
                } else if (this.dateRange.dateType === DateQueryDateTypeEnum.HARVESTED) {
                    facet.dateField = NeoFacetDateFieldApiEnum.TIMESTAMP;
                }
            }
        });

        // Update neoFacets for saved mentions
        this.updateNeoFacetsForSavedMentions();

        this.setNeoFacetsFromChartSettings();
    }
    get chartType(): ReportBlockTypeEnum {
        return this._chartType;
    }

    /**
     * Class constructor
     */
    constructor(
        apiModel?: ReportSeriesApiModel | SharedReportSeriesApiModel,
        chartType?: ReportBlockTypeEnum,
        seriesType?: SeriesTypeEnum,
        metric?: ChartMetricsEnum,
        chartDisplayElement?: ChartDisplayElementModel
    ) {
        if (apiModel) {
            this.id = apiModel.id;
            this.label = apiModel.label;
            this.color = apiModel.color;
            this.mentions = {};
            this.setMentionsIds(apiModel);
            this.externalData = apiModel.externalDataSource;

            if (apiModel.sourceType) {
                this.type = seriesSourceTypesToSeriesTypesMap.get(apiModel.sourceType);
            }

            if (this.externalData) {
                if (this.externalData.externalDataType === DataConnectorTypesEnum.GOOGLE_ANALYTICS
                    || this.externalData.externalDataType === DataConnectorTypesEnum.GOOGLE_ANALYTICS_4) {
                    this.type = SeriesTypeEnum.googleAnalytics;
                }
            }

            if (!this.type) {
                if (seriesType) {
                    this.type = seriesType;
                } else {
                    this.type = SeriesTypeEnum.folder;
                }
            }

            this.mentions.searchCriteriaId = apiModel.filterId;
            this.dateRange = new ReportDateRangeModel(apiModel.dateRange);
            this.styles = new GenericStyleModel(apiModel.styles);

            // check needs to be done before selecting the prominence subject (on new report)
            if (apiModel.prominenceGroupId && this.styles['prominenceGroupName'] && this.styles['prominenceGroupKeywords']) {
                this.prominenceTrendGroup = {
                    id: apiModel.prominenceGroupId,
                    // Absolutely a hack until reporting backend is redone
                    // TODO : remove
                    name: this.styles['prominenceGroupName'],
                    keywords: this.styles['prominenceGroupKeywords']
                };
            }

            if (this.type === SeriesTypeEnum.googleAnalytics && this.externalData) {
                this.externalData.googleAnalyticsAccountId = this.styles.googleAnalyticsAccountId;
                this.externalData.googleAnalyticsPropertyId = this.styles.googleAnalyticsPropertyId;

                if (chartDisplayElement) {
                    this.referralTypes = chartDisplayElement.referralTypes;
                }
            }

            if (chartDisplayElement) {
                this.prEvents = chartDisplayElement.prevents;
                this.metrics = chartDisplayElement.metrics;
                this.termType = chartDisplayElement.termType;
                this.numOfItems = chartDisplayElement.numOfItems;
                this.chartSpecificType = chartDisplayElement.chartSpecificType;
            }

            if (chartDisplayElement?.chartHeaderSettings) {
                this.hasPreviousPeriod = chartDisplayElement.chartHeaderSettings?.previousPeriod;
            }

            if (this.styles.networkSelection && chartDisplayElement) {
                chartDisplayElement.networkSelection = this.styles.networkSelection;
            }
        } else {
            this.id = null;
            this.label = null;
            this.mentions = {};
            this.setMentionsIds();
            this.mentions.filters = new MentionSearchModel();
            this.dateRange = new ReportDateRangeModel();
            this.styles = new GenericStyleModel();

            this.type = seriesType || SeriesTypeEnum.folder;
        }

        this.metric = metric;

        if (chartType) {
            this.chartType = chartType;
            if (chartType === ReportBlockTypeEnum.REFERRAL_TREND) {
                this.type = SeriesTypeEnum.googleAnalytics;
            }
            if (!this.type && chartType && chartType === ReportBlockTypeEnum.COVERAGE_COMPARISON) {
                this.type = SeriesTypeEnum.topic;
            }
        }

        if (this.chartType === ReportBlockTypeEnum.RECENT_TOPICS) {
            this.type = SeriesTypeEnum.topic;
        }

        if ((chartType === ReportBlockTypeEnum.TOP_SOCIAL_REFERRERS ||chartType === ReportBlockTypeEnum.TOP_REFERRAL_SOURCES)
            && this.metric && this.externalData) {
            const metricsToRequestTypesMap = chartType === ReportBlockTypeEnum.TOP_REFERRAL_SOURCES ?
                externalChartMetricToTopReferralSourcesReportRequestTypeMap : externalChartMetricToTopSocialReferrersReportRequestTypeMap;
            const newReportRequestType = metricsToRequestTypesMap.get(this.metric as ExternalChartMetricsEnum);
            this.externalData.googleAnalyticsReportType = newReportRequestType;
        }

        this.sentiments = [];

        if (!this.referralTypes) {
            this.referralTypes = [];
        }

        if (!this.prEvents) {
            this.prEvents = [];
        }

        if (!this.metrics) {
            this.metrics = [];
        }

        this.dataProvider = new BehaviorSubject<DataPointModel[]>(null);
        this.previousPeriodDataProvider = new BehaviorSubject<DataPointModel[]>(null);
        this.mentionProvider = new BehaviorSubject<Mention[]>(null);

        /** If this is a Shared Report */
        if (apiModel && 'seriesData' in apiModel) {
            this.isSharedReport = true;
            const serieData = JSON.parse(apiModel.seriesData);

            if (Array.isArray(serieData)) {
                this.dataProvider.next(serieData);
            } else {
                this.dataProvider.next(serieData?.currentPeriodData);
                this.previousPeriodDataProvider.next(serieData?.previousPeriodData);
            }

            if (SortableMentionBlockTypeEnum[this.chartType] && apiModel.seriesMentionsData) {
                this.useMentionProvider = true;
                this.mentionProvider.next(JSON.parse(apiModel.seriesMentionsData));
            }
        }

        if (!this.isSharedReport && SortableMentionBlockTypeEnum[this.chartType]) {
            this.useMentionProvider = true;
            this.mentionProvider = new BehaviorSubject<Mention[]>(null);
        }

        this.seriesUniqueUuid = uuid.v4();
    }

    public static getSeriesColorKeyPrefix(): string {
        return 'seriesColor';
    }

    public updateNeoFacetsForSavedMentions(): void {
        if (!(mentionSeriesTypes.includes(this.type) && this.isSavedSource())) {
            return;
        }
        // having engagementBucketRequests part of the payload for saved mentions neo facets gives you a 500
        this.mentions.neoFacets?.map(neoFacet => {
            delete neoFacet.engagementBucketRequests;
        });

        switch(this._chartType) {
            case ReportBlockTypeEnum.SHARE_OF_VOICE:
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME:
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME_COMPARISON:
            case ReportBlockTypeEnum.COVERAGE_COMPARISON:
            case ReportBlockTypeEnum.SOCIAL_COVERAGE_BY_NETWORK:
                const neoFacetKey = this._chartType === ReportBlockTypeEnum.SOCIAL_COVERAGE_BY_NETWORK ? ReportsNeoFacetKeys.socialCoverageByNetwork : ReportsNeoFacetKeys.overtimeFacet;
                const neoFacet = this.mentions.neoFacets?.find(facet => facet.key === neoFacetKey);
                if (neoFacet) {
                    const bucketRequests = neoFacet.aggregateBucketRequests || [];
                    const keySet: Set<ReportsNeoFacetKeys> = new Set(bucketRequests.map(request => request.key as ReportsNeoFacetKeys));
                    const filteredRequests = socialMetricsAggregateBucketRequests.filter(
                        request => !keySet.has(request.key as ReportsNeoFacetKeys)
                    );
                    bucketRequests.push(...filteredRequests);
                    if (this._chartType === ReportBlockTypeEnum.SOCIAL_COVERAGE_BY_NETWORK) {
                        const engagementsBucketRequest = bucketRequests.find(request => request.key === ReportsNeoFacetKeys.engagementsOverTimeFacet);
                        if (engagementsBucketRequest) {
                            engagementsBucketRequest.key = ReportsNeoFacetKeys.mediaTypeEngagements;
                        }
                    }
                    neoFacet.aggregateBucketRequests = bucketRequests;
                }
                break;
            case ReportBlockTypeEnum.COVERAGE_METRIC: {
                const overtimeNeoFacet = this.mentions.neoFacets?.find(facet => facet.key === ReportsNeoFacetKeys.overtimeFacet);
                if (overtimeNeoFacet) {
                    const bucketRequests = overtimeNeoFacet.aggregateBucketRequests || [];
                    const keySet: Set<ReportsNeoFacetKeys> = new Set(bucketRequests.map(request => request.key as ReportsNeoFacetKeys));
                    const filteredRequests = socialMetricsAggregateBucketRequests.filter(
                        request => !keySet.has(request.key as ReportsNeoFacetKeys)
                    );
                    bucketRequests.push(...filteredRequests);
                    overtimeNeoFacet.aggregateBucketRequests = bucketRequests;
                }
                break;
            }
            case ReportBlockTypeEnum.COVERAGE_SUMMARY:
                const engagementsFacet = this.mentions.neoFacets?.find(facet => facet.key === ReportsNeoFacetKeys.mediaTypeEngagements);
                if (engagementsFacet) {
                    const bucketRequest: EngagementBucketRequestApiModel[] = cloneDeep(engagementsNeoFacet.engagementBucketRequests) || [];
                    bucketRequest?.forEach(item => delete item.engagementAge);
                    engagementsFacet.aggregateBucketRequests = bucketRequest;
                }
        }
    }

    /**
     * Converts this front end model to the back end version
     */
    public toApiModel(anonymize: boolean = false): ReportSeriesApiModel {
        // Absolutely a hack until reporting backend is redone
        // TODO : remove
        if (this.prominenceTrendGroup) {
            this.styles['prominenceGroupName'] = this.prominenceTrendGroup?.name;
            this.styles['prominenceGroupKeywords'] = this.prominenceTrendGroup?.keywords;
        }
        if (this.type === SeriesTypeEnum.googleAnalytics) {
            this.styles.googleAnalyticsAccountId = this.externalData?.googleAnalyticsAccountId;
            this.styles.googleAnalyticsPropertyId = this.externalData?.googleAnalyticsPropertyId;
        }

        return {
            id: anonymize ? null : this.id,
            label: this.label,
            color: this.color,
            sourceType: seriesTypesToSeriesSourceTypesMap.get(this.type),
            sourceId: this.getSeriesSourceId(),
            prominenceGroupId: this.prominenceTrendGroup?.id,
            filterId: anonymize ? null : this.mentions.searchCriteriaId,
            externalDataSource: this.externalData,
            dateRange: this.dateRange?.toApiModel ? this.dateRange.toApiModel() : null,
            styles: JSON.stringify(this.styles.toApi())
        };
    }

    /**
     * Converts this front end model to the back end version
     */
    public toDashboardApiModel(): DashboardSeriesApiModel {
        return {
            type: this.type,
            color: this.color,
            dateRange: this.dateRange?.toApiModel ? this.dateRange.toApiModel() : null
        };
    }

    public toSharedReportSeriesApiModel(): SharedReportSeriesApiModel {
        if ((this.dataProvider.getValue() === null) && (this.mentionProvider.getValue() === null)) {
            // Bad data cases e.g. topic was deleted
            return null;
        }

        // Absolutely a hack until reporting backend is redone
        // TODO : remove
        this.styles['prominenceGroupName'] = this.prominenceTrendGroup?.name;
        this.styles['prominenceGroupKeywords'] = this.prominenceTrendGroup?.keywords;
        this.styles['subSeries'] = this.subSeries?.slice();

        let seriesData;
        let seriesMentionsData;

        if (this.hasPreviousPeriod) {
            const previousPeriodData = this.previousPeriodDataProvider.getValue();
            previousPeriodData.forEach(point => delete point.viewUrl);
            seriesData = JSON.stringify({
                currentPeriodData: this.dataProvider.getValue(),
                previousPeriodData
            });
        } else {
            const currentPeriodData = this.dataProvider.getValue();
            if (this.chartType === ReportBlockTypeEnum.SENTIMENT_TREND) {
                currentPeriodData.forEach(point => {
                    Object.values(SearchCriteriaQuerySentimentsEnum).forEach(sentiment => {
                        delete point[`viewMentionUrl_${sentiment}`];
                    });
                });
            }
            seriesData = JSON.stringify(this.dataProvider.getValue());
        }

        if (this.useMentionProvider) {
            seriesMentionsData = JSON.stringify(this.mentionProvider.getValue());
        }

        const sharedReportSeries = {
            id: this.id,
            label: this.label,
            sourceType: seriesTypesToSeriesSourceTypesMap.get(this.type),
            sourceId: this.getSeriesSourceId(),
            prominenceGroupId: this.prominenceTrendGroup?.id,
            filterId: this.mentions.searchCriteriaId,
            externalDataSource: this.externalData,
            dateRange: this.dateRange?.toApiModel ? this.dateRange.toApiModel() : null,
            styles: JSON.stringify(this.styles.toApi()),
            seriesData,
            seriesMentionsData
        } as SharedReportSeriesApiModel;

        if (this.chartSpecificType) {
            sharedReportSeries.chartSpecificType = this.chartSpecificType;
        }

        return sharedReportSeries;
    }

    /**
     * The series key is a string used in the map of series info, it's built to be unique to the data source
     */
    public getSeriesKey(ignoreDate: boolean = false): string {
        let key: any;
        const filters = this.mentions?.filters?.clone();
        // includedRegions and excludedRegions are only used to show the user the selected filters
        // The original values are saved in codes which have no meaning to the user, so we translate them to actual country names
        // But this translation happens after we load the data, causing the data to look dirty,
        // so by removing the part that is only used to show the full name to the user, we prevent the issue of data looking dirty while it's not
        delete filters?.searchCriteria?.query?.includedRegions;
        delete filters?.searchCriteria?.query?.excludedRegions;

        // HACK: need to remove enableEngagementInsights, because on clone series, it doesn't exist in cloned one, so it doesn't match
        delete filters?.searchCriteria?.query?.enableEngagementInsights;
        if (mentionSeriesTypes.includes(this.type)) {
            key = {
                topicId: this.mentions.topicId,
                mediaSubscriptionId: this.mentions.mediaSubscriptionId,
                folderId: this.mentions.folderId,
                tagId: this.mentions.tagId,
                tagGroupId: this.mentions.tagGroupId,
                sourceGroupId: this.mentions.sourceGroupId,
                aiAgentId: this.mentions.aiAgentId,
                prominenceTrendGroupId: this.prominenceTrendGroup?.id,
                searchCriteria: filters,
                isPreviousPeriod: this.mentions.isPreviousPeriod ? this.mentions.isPreviousPeriod : false, // we want to show false even if it's undefined (fixes CIB isEqual)
                sortField: this.sortField,
            };
        } else if (socialSeriesTypes.includes(this.type)) {
            key = {
                socialTopicId: this.mentions.socialTopicId,
                themeId: this.mentions.themeId,
                prominenceTrendGroupId: this.prominenceTrendGroup?.id,
                searchCriteria: filters,
                isPreviousPeriod: this.mentions.isPreviousPeriod ? this.mentions.isPreviousPeriod : false, // we want to show false even if it's undefined (fixes CIB isEqual)
                sortField: this.sortField,
            };
        } else if (externalDataSeriesTypes.includes(this.type)) {
            key = { ...this.externalData };
        }

        if (!ignoreDate) {
            key.dateRange = this.dateRange;
        }

        if (this.termType) {
            key.termType = this.termType;
        }

        // @TODO: Investigate hashing this first to keep the key smaller
        return JSON.stringify(key);
    }

    /**
     * Gets the appropriate search criteria for this series, if two series have the same seriesKey, this should also be the same
     */
    public toSearchCriteriaModel(): SearchCriteriaModel {
        let search: MentionSearchCriteriaModel;
        const clone: ReportSeriesModel = this.clone();

        if (clone.mentions) {
            if (clone.mentions.topic) {
                search = clone.mentions.topic.searchCriteria;
            } else {
                search = new MentionSearchCriteriaModel();
            }

            if (clone.mentions.mediaSubscriptionId) {
                search.query.subscription.include = [clone.mentions.mediaSubscriptionId];
            }
            if (clone.mentions.folderId) {
                search.query.folder.include = [clone.mentions.folderId];
            }
            if (clone.mentions.tagId) {
                search.query.tag.include = [clone.mentions.tagId];
            }
            if (clone.mentions.tagGroupId) {
                search.query.tagGroup.include = [ clone.mentions.tagGroupId ];
            }
            if (clone.mentions.sourceGroupId) {
                search.query.sourceGroup.include = [ clone.mentions.sourceGroupId ];
            }
            if (clone.mentions.aiAgentId) {
                search.query.aiAgent = { include: [ clone.mentions.aiAgentId ], exclude: [] };
            }
            if (clone.mentions.socialTopicId) {
                search.query.agent.include = [clone.mentions.socialTopicId];
            }
            if (clone.mentions.themeId) {
                search.query.theme.include = [clone.mentions.themeId];
            }

            if (clone.mentions.filters?.searchCriteria) {
                search.showSimilar = clone.mentions.filters.searchCriteria.showSimilar;
                search.savedFilterIds = clone.mentions.filters.searchCriteria.savedFilterIds;
                if (clone.mentions.filters?.searchCriteria?.query && !clone.mentions.filters.searchCriteria.query.isEmpty()) {
                    search.filterQuery = clone.mentions.filters.searchCriteria.query;
                    search.filterQuery.dateQuery = null;
                } else {
                    search.filterQuery = null;
                }
            }
        }
        search.setMinimumQueryForFacets();
        search.query.dateQuery = clone.dateRange;
        if (this.prominenceTrendGroup ) {
            search.prominenceByReachRequest = new ProminenceByReachRequestModel(this.prominenceTrendGroup.id);
        }

        if (search.query.dateQuery.type === DateQueryTypeEnum.RELATIVE) {
            if (search.query.dateQuery.relativeStartOffsetH !== -24) {
                // @HACK adding 10 second to get an extra slice
                // the odd time on prod (the previous 1 second) is not enough (also fixes slow connection)

                // scenario: if we select a date (Last 3 Days) for a line chart, we get 3 data points from the api,
                // but before we display the chart, we filter out all the data points outside the range of those 3 days
                // so after the api return, NOW is later than the start of the API request, so if a slow connection,
                // or a the call hangs, we filter out the first data point, 10 seconds gives us plenty of time to work with
                search.query.dateQuery.relativeStartOffsetH -= 0.0028;
            }
        }

        if (this.chartType === ReportBlockTypeEnum.TOP_POSTS) {
            search.pageSize = 5;

            if (this.styles?.networkSelection?.length && search.query?.includedMediaTypes) {
                search.query.includedMediaTypes = search.query.includedMediaTypes.filter(mediaType => this.styles?.networkSelection.includes(mediaType.id));
            }
        } else {
            search.pageSize = 0;
        }
        if (this.sortField) {
            search.sortField = this.sortField as SearchCriteriaSortFieldEnum;

            if (search.sortField === SearchCriteriaSortFieldEnum.function) {
                search.sortFunction = {
                    aggregateFunction: AggregateFunctionApiEnum.SUM,
                    aggregateFields: [AggregateFieldsApiEnum.QUOTE, AggregateFieldsApiEnum.REPLY],
                };
            }
        }

        search.dateFacets = clone.getDateFacetModel(search);
        for (let i = 0; i < search.dateFacets.length; i++) {
            // we don't want to api to be making requests for ALL the facets defined.. only depending on the metric selected
            // @TODO: replace to use getAggregateDateFacetsByMetrics
            if (clone.metric) {
                if (clone.metric === ChartMetricsEnum.IMPRESSIONS) {
                    search.dateFacets[i].aggregateFacets = clone.mentions.aggregateDateFacets.filter(facetTypes => {
                        return facetTypes === AggregateDateFacetTypesEnum.SUM_AUDIENCE_REACH || facetTypes === AggregateDateFacetTypesEnum.AVG_AUDIENCE_REACH;
                    });
                } else if (clone.metric === ChartMetricsEnum.ave) {
                    search.dateFacets[i].aggregateFacets = clone.mentions.aggregateDateFacets.filter(facetTypes => {
                        return facetTypes === AggregateDateFacetTypesEnum.SUM_AD_VALUE_USD || facetTypes === AggregateDateFacetTypesEnum.AVG_AD_VALUE_USD;
                    });
                } else {
                    search.dateFacets[i].aggregateFacets = clone.mentions.aggregateDateFacets;
                }
            } else {
                search.dateFacets[i].aggregateFacets = clone.mentions.aggregateDateFacets;
            }

            if (clone.metrics?.length > 0) {
                search.dateFacets[i].aggregateFacets = this.getAggregateDateFacetsByMetrics(clone.metrics, clone.mentions.aggregateDateFacets);
            }
        }
        search.neoFacets = clone.mentions.neoFacets || [];

        search.facets = clone.mentions.facetTypes;
        // special settings for wordcloud search
        if (this.termType) {
            if (this.metric === ChartMetricsEnum.VOLUME) {
                search.isWordCloudRequest = true;
                search.pageSize = 100;
            }
            search.neoFacets = this.getTopTermsChartNeoFacets();
        }

        // having engagementBucketRequests part of the payload for saved mentions neo facets gives you a 500
        if (mentionSeriesTypes.includes(this.type) && this.isSavedSource()) {
            search.neoFacets?.map(neoFacet => {
                delete neoFacet.engagementBucketRequests;
            });
        }

        return search.getApiEntity();
    }



    /**
     * this function is for CIB, we want the facets, neofacets etc etc with the mentions.filters MentionSearchModel
     */
    public cibFilterSearchCriteriaModel(): MentionSearchCriteriaModel {
        const search: MentionSearchCriteriaModel = new MentionSearchCriteriaModel();
        const clone: ReportSeriesModel = this.clone();
        if (!clone.mentions.filters?.searchCriteria) {
            return search;
        }

        if (clone.mentions) {
            if (clone.mentions.filters?.searchCriteria) {
                search.showSimilar = clone.mentions.filters.searchCriteria.showSimilar;
                search.savedFilterIds = clone.mentions.filters.searchCriteria.savedFilterIds;

                search.filterQuery = null;
                if (clone.mentions.filters?.searchCriteria?.query && !clone.mentions.filters.searchCriteria.query.isEmpty()) {
                    search.query = clone.mentions.filters.searchCriteria.query;
                }
            }
        }
        search.setMinimumQueryForFacets();
        search.query.dateQuery = clone.dateRange;
        if (this.prominenceTrendGroup) {
            search.prominenceByReachRequest = new ProminenceByReachRequestModel(this.prominenceTrendGroup.id);
        }

        if (search.query.dateQuery.type === DateQueryTypeEnum.RELATIVE) {
            if (search.query.dateQuery.relativeStartOffsetH !== -24) {
                // @HACK adding 10 second to get an extra slice
                // the odd time on prod (the previous 1 second) is not enough (also fixes slow connection)

                // scenario: if we select a date (Last 3 Days) for a line chart, we get 3 data points from the api,
                // but before we display the chart, we filter out all the data points outside the range of those 3 days
                // so after the api return, NOW is later than the start of the API request, so if a slow connection,
                // or a the call hangs, we filter out the first data point, 10 seconds gives us plenty of time to work with
                search.query.dateQuery.relativeStartOffsetH -= 0.0028;
            }
        }

        if (this.chartType === ReportBlockTypeEnum.TOP_POSTS) {
            search.pageSize = 5;

            if (this.styles?.networkSelection?.length && search.query?.includedMediaTypes) {
                search.query.includedMediaTypes = search.query.includedMediaTypes.filter(mediaType => this.styles?.networkSelection.includes(mediaType.id));
            }
        } else {
            search.pageSize = 0;
        }
        if (this.sortField) {
            search.sortField = this.sortField as SearchCriteriaSortFieldEnum;

            if (search.sortField === SearchCriteriaSortFieldEnum.function) {
                search.sortFunction = {
                    aggregateFunction: AggregateFunctionApiEnum.SUM,
                    aggregateFields: [AggregateFieldsApiEnum.QUOTE, AggregateFieldsApiEnum.REPLY],
                };
            }
        }

        search.dateFacets = clone.getDateFacetModel(search);
        for (let i = 0; i < search.dateFacets.length; i++) {
            // we don't want to api to be making requests for ALL the facets defined.. only depending on the metric selected
            // @TODO: replace to use getAggregateDateFacetsByMetrics
            if (clone.metric) {
                if (clone.metric === ChartMetricsEnum.IMPRESSIONS) {
                    search.dateFacets[i].aggregateFacets = clone.mentions.aggregateDateFacets.filter(facetTypes => {
                        return facetTypes === AggregateDateFacetTypesEnum.SUM_AUDIENCE_REACH || facetTypes === AggregateDateFacetTypesEnum.AVG_AUDIENCE_REACH;
                    });
                } else if (clone.metric === ChartMetricsEnum.ave) {
                    search.dateFacets[i].aggregateFacets = clone.mentions.aggregateDateFacets.filter(facetTypes => {
                        return facetTypes === AggregateDateFacetTypesEnum.SUM_AD_VALUE_USD || facetTypes === AggregateDateFacetTypesEnum.AVG_AD_VALUE_USD;
                    });
                } else {
                    search.dateFacets[i].aggregateFacets = clone.mentions.aggregateDateFacets;
                }
            } else {
                search.dateFacets[i].aggregateFacets = clone.mentions.aggregateDateFacets;
            }

            if (clone.metrics?.length > 0) {
                search.dateFacets[i].aggregateFacets = this.getAggregateDateFacetsByMetrics(clone.metrics, clone.mentions.aggregateDateFacets);
            }
        }
        search.neoFacets = clone.mentions.neoFacets || [];

        const dateQuery = search.query.dateQuery;
        for (const neoFacetRequest of search.neoFacets) {
            if (!neoFacetRequest.dateField) {
                continue;
            }

            let startISOTimeString: string = '';
            let endISOTimeString: string = '';
            if (dateQuery?.type === DateQueryTypeEnum.RELATIVE) {
                startISOTimeString = moment().add(dateQuery.relativeStartOffsetH, 'hours').toISOString();
                endISOTimeString = moment().add(dateQuery.relativeEndOffsetH, 'hours').toISOString();
            } else if (dateQuery?.type === DateQueryTypeEnum.ABSOLUTE) {
                startISOTimeString = moment(dateQuery.absoluteRange.start).toISOString();
                endISOTimeString = moment(dateQuery.absoluteRange.end).toISOString();
            } else if (dateQuery?.type === DateQueryTypeEnum.RELATIVE_TIMEZONE) {
                startISOTimeString = DateQueryModel.getStartDateForRelativeTimezone(
                    dateQuery.relativeTimezoneDateType, dateQuery.timezone
                ).toISOString();
                endISOTimeString = DateQueryModel.getEndDateForRelativeTimezone(dateQuery.relativeTimezoneDateType, dateQuery.timezone).toISOString();
            } else {
                // doesn't reach here now that we added the if statement..
                // throw Error('Can\'t generate date neo facet for all');
            }

            neoFacetRequest.range = {
                start: startISOTimeString,
                end: endISOTimeString
            };

            if (!clone.timeInterval || clone.timeInterval === ChartTimeIntervalsEnum.DYNAMIC) {
                if (DateQueryModel.getTotalHoursInApiQuery((dateQuery as ReportDateRangeModel).toSearchQueryApiModel()) > 24
                    && dateQuery.relativeTimezoneDateType !== RelativeTimezoneDateTypeEnum.LAST_24_HOURS
                ) {
                    neoFacetRequest.gap = 86400000; // 24 hours * 60 minutes * 60 seconds * 1000 milliseconds
                } else {
                    neoFacetRequest.gap = 3600000; // 60 minutes * 60 seconds * 1000 milliseconds
                }
            } else {
                switch (clone.timeInterval) {
                    case ChartTimeIntervalsEnum.WEEK:
                        neoFacetRequest.gap = 86400000 * 7;
                        break;
                    case ChartTimeIntervalsEnum.MONTH:
                        neoFacetRequest.gap = 86400000 * 30;
                        break;
                    case ChartTimeIntervalsEnum.QUARTER:
                        neoFacetRequest.gap = 86400000 * 90;
                        break;
                    case ChartTimeIntervalsEnum.YEAR:
                        neoFacetRequest.gap = 31556926000;
                        break;
                }
            }
        }

        search.facets = clone.mentions.facetTypes;
        // special settings for wordcloud search
        if (this.termType) {
            if (this.metric === ChartMetricsEnum.VOLUME) {
                search.isWordCloudRequest = true;
                search.pageSize = 100;
            }
            search.neoFacets = this.getTopTermsChartNeoFacets();
        }

        // having engagementBucketRequests part of the payload for saved mentions neo facets gives you a 500
        if (mentionSeriesTypes.includes(this.type) && this.isSavedSource()) {
            search.neoFacets?.map(neoFacet => {
                delete neoFacet.engagementBucketRequests;
            });
        }

        if (search.query.includedMediaTypes?.length === Object.keys(MediaTypeEnum).length) {
            search.query.includedMediaTypes = [];
        }
        if (search.query.sentiments?.length === Object.keys(SearchCriteriaQuerySentimentsEnum).length) {
            search.query.sentiments = [];
        }

        return search;
    }

    public getTopTermsChartNeoFacets(): NeoFacetRequestApiModel[] {
        let neoFacets: NeoFacetRequestApiModel[] = [];
        switch (this.metric) {
            case ChartMetricsEnum.VOLUME:
                if (this.termType !== TermTypesEnum.WORDS) {
                    neoFacets = [{
                        key: this.termType + this.metric,
                        engagementBucketRequests: [],
                        prefix: termTypesToNeoFacetPrefixMap.get(this.termType),
                        termField: NeoFacetTermFieldApiEnum.MENTIONS,
                        aggregateBucketRequests: []
                    }];
                }
                break;
            case ChartMetricsEnum.ave:
            case ChartMetricsEnum.audienceReach:
            case ChartMetricsEnum.IMPRESSIONS:
            case ChartMetricsEnum.ENGAGEMENTS:
                neoFacets = [{
                    key: this.termType + this.metric,
                    prefix: termTypesToNeoFacetPrefixMap.get(this.termType),
                    aggregateBucketRequests: [
                        {
                            field: chartMetricsToNeoFacetTermFieldMap.get(this.metric),
                            key: this.termType + this.metric,
                            function: BucketFunctionApiEnum.INDIVIDUAL_SUM
                        }
                    ],
                    termField: NeoFacetTermFieldApiEnum.MENTIONS,
                    termSort: {
                        aggregateBucketKey: this.termType + this.metric,
                        sortBy: NeoFacetSortByEnum.aggregate,
                        sortOrder: SortOrderEnum.descending
                    }
                }];
                break;
            case ChartMetricsEnum.APPLAUSE:
            case ChartMetricsEnum.AMPLIFICATION:
            case ChartMetricsEnum.CONVERSATION:
            case ChartMetricsEnum.VIEWS:
                const neoFacet: NeoFacetRequestApiModel = {
                    key: this.termType + this.metric,
                    prefix: termTypesToNeoFacetPrefixMap.get(this.termType),
                    termField: NeoFacetTermFieldApiEnum.MENTIONS,
                    termSort: {
                        aggregateBucketKey: this.termType + this.metric,
                        sortBy: NeoFacetSortByEnum.aggregate,
                        sortOrder: SortOrderEnum.descending
                    }
                };
                // For saved mentions
                if (mentionSeriesTypes.includes(this.type) && this.isSavedSource()) {
                    neoFacet.aggregateBucketRequests = [{
                        field: chartMetricsToNeoFacetTermFieldMap.get(this.metric),
                        key: this.termType + this.metric,
                        function: BucketFunctionApiEnum.INDIVIDUAL_SUM
                    }];
                } else { // For social mentions
                    neoFacet.engagementBucketRequests = [{
                        field: chartMetricsToNeoFacetTermFieldMap.get(this.metric),
                        key: this.termType + this.metric,
                        engagementAge: EngagementAgeApiEnum.LATEST,
                        function: BucketFunctionApiEnum.INDIVIDUAL_SUM
                    }];
                }
                neoFacets = [neoFacet];
                break;
        }

        // having engagementBucketRequests part of the payload for saved mentions neo facets gives you a 500
        if (mentionSeriesTypes.includes(this.type) && this.isSavedSource()) {
            neoFacets?.map(neoFacet => {
                delete neoFacet.engagementBucketRequests;
            });
        }

        return neoFacets;

    }

    getAggregateDateFacetsByMetrics(metrics: ChartMetricsEnum[], aggregateDateFacets: AggregateDateFacetTypesEnum[]): AggregateDateFacetTypesEnum[] {
        let facets = [];
        metrics.forEach(metric => {
            if (metric) {
                if (metric === ChartMetricsEnum.volumeOfMentions) {
                    facets = facets;
                } else if (metric === ChartMetricsEnum.IMPRESSIONS) {
                    facets.push(...aggregateDateFacets.filter(facetTypes => {
                        return facetTypes === AggregateDateFacetTypesEnum.SUM_AUDIENCE_REACH || facetTypes === AggregateDateFacetTypesEnum.AVG_AUDIENCE_REACH;
                    }));
                } else if (metric === ChartMetricsEnum.ave) {
                    facets.push(...aggregateDateFacets.filter(facetTypes => {
                        return facetTypes === AggregateDateFacetTypesEnum.SUM_AD_VALUE_USD || facetTypes === AggregateDateFacetTypesEnum.AVG_AD_VALUE_USD;
                    }));
                } else {
                    facets = union(facets, aggregateDateFacets);
                }
            } else {
                facets.push(...aggregateDateFacets);
            }
        });
        return facets;
    }

    /**
     * Compare this model to another of it's kind
     */
    public isEqual(other: ReportSeriesModel, compareGlobalSource: boolean = false): boolean {
        if (!other) {
            return false;
        }
        if (!compareGlobalSource) {
            if (this.id !== other.id) {
                return false;
            }
            if (this.label !== other.label) {
                return false;
            }
            if (this.color !== other.color) {
                return false;
            }
            if (!this.dateRange.isEqual(other.dateRange)) {
                return false;
            }
        }

        if (this.getSeriesKey(true) !== other.getSeriesKey(true)) {

            return false;
        }

        if (!this.styles.equals(other.styles, true)) {

            return false;
        }

        return true;
    }

    /**
     * Returns a copy of this ReportSeriesModel
     */
    public clone(chartType: ReportBlockTypeEnum = this.chartType): ReportSeriesModel {
        const clone = new ReportSeriesModel(this.toApiModel(), chartType, this.type, this.metric);
        if (this.seriesUniqueUuid) {
            clone.seriesUniqueUuid = this.seriesUniqueUuid;
        }
        clone.mentions.topic = this.mentions.topic?.clone(false);
        clone.mentions.combinedQuery = this.mentions.combinedQuery?.clone();
        clone.mentions.neoFacets = JSON.parse(JSON.stringify(this.mentions.neoFacets || []));
        clone.mentions.isPreviousPeriod = this.mentions.isPreviousPeriod;
        clone.mentions.mentionRequestGenerator = this.mentions.mentionRequestGenerator;
        clone.mentions.mentionDataMapper = this.mentions.mentionDataMapper;
        clone.mentions.facetGenerator = this.mentions.facetGenerator;
        clone.mentions.dataMapper = this.mentions.dataMapper;
        clone.mentions.asyncDataMapper = this.mentions.asyncDataMapper;
        clone.sentiments = this.sentiments.slice();
        clone.referralTypes = this.referralTypes.slice();
        clone.prEvents = this.prEvents.slice();
        clone.color = this.color;
        clone.appendCombineQuery = this.appendCombineQuery;
        clone.metrics = this.metrics?.slice();
        clone.termType = this.termType;
        clone.numOfItems = this.numOfItems;
        clone.chartSpecificType = this.chartSpecificType;
        clone.hasPreviousPeriod = this.hasPreviousPeriod;
        clone.subSeries = this.subSeries?.slice();
        clone.sortField = this.sortField;
        clone.fullSearch = this.fullSearch?.clone();
        clone.key = this.key;
        clone.timeInterval = this.timeInterval;
        if (this.chartType) {
            clone.chartType = this.chartType;
        }

        if (this.mentions.filters) {
            clone.mentions.filters = this.mentions.filters.clone(false);

            // @HACK: The clone method on the search should be changed to do a full clone and not change regions to raw
            clone.mentions.filters.searchCriteria.query.includedRegions = this.mentions.filters.searchCriteria.query.includedRegions.map(srcRegion => {
                if (srcRegion?.toApiEntity) {
                    return new JournodexGeographyModel(srcRegion.toApiEntity());
                } else {
                    return new JournodexGeographyModel(srcRegion);
                }
            });
            clone.mentions.filters.searchCriteria.query.rawIncludedRegions = null;
        }

        try {
            clone.dataProvider = new BehaviorSubject<DataPointModel[]>(this.dataProvider.getValue());
            clone.previousPeriodDataProvider = new BehaviorSubject<DataPointModel[]>(this.previousPeriodDataProvider.getValue());
        } catch (e) {
            // if the data provider contains an error, (added via Subject::error) this will throw, which prevents this function
            // from returning. commonly this occurs if the data source is no longer available
        }

        clone.cacheResult = this.cacheResult;
        if (this.searchResults) {
            clone.searchResults = this.searchResults.clone();
        }

        return clone;
    }

    /**
     * Returns a minimal clone of the series
     * currently will be used on the dashboard page, because we want to send very little data to the backend on save
     */
    public partialClone(chartType: ReportBlockTypeEnum = this.chartType): ReportSeriesModel {
        const clone = new ReportSeriesModel(null, chartType, this.type, this.metric);
        clone.color = this.color;
        clone.dateRange = this.dateRange;
        clone.dataIndex = this.dataIndex;

        return clone;
    }

    /**
     * Based on the MentionSearchCriteriaModel, returns the appropriate DateFaceModel[] to request
     */
    private getDateFacetModel(searchCriteria: MentionSearchCriteriaModel): DateFacetModel[] {
        if (
            !this.prominenceTrendGroup &&
            (!this.mentions.aggregateDateFacets || this.mentions.aggregateDateFacets.length === 0)
        ) {
            return [];
        }

        let field: DateFacetTypesEnum;
        let dateQuery: DateQueryModel;

        if (searchCriteria.query.dateQuery) {
            dateQuery = searchCriteria.query.dateQuery;
            field = dateTypeToDateFacetMap.get(searchCriteria.query.dateQuery.dateType);
        } else {
            field = DateFacetTypesEnum.indexed;
        }

        if (!dateQuery || dateQuery.type === DateQueryTypeEnum.ALL) {
            return [this.getDailyDateFacet(field)];
        } else if (dateQuery.type === DateQueryTypeEnum.ABSOLUTE) {
            if (dateQuery.absoluteDate) {
                // single day
                return [this.getHourlyDateFacet(field)];
            }
            // range
            const start = moment(dateQuery.absoluteRange.start);
            const end = moment(dateQuery.absoluteRange.end);

            const hoursDifference = moment.duration(end.diff(start)).asHours();

            if (hoursDifference <= 24) {
                return [this.getHourlyDateFacet(field)];
            } else {
                return [this.getDailyDateFacet(field)];
            }
        } else if (dateQuery.type === DateQueryTypeEnum.RELATIVE_TIMEZONE) {
            const hoursDifference = DateQueryModel.getRelativeTimezoneDiffAsHours(dateQuery.relativeTimezoneDateType, dateQuery.timezone);
            if (hoursDifference > 24 && dateQuery.relativeTimezoneDateType !== RelativeTimezoneDateTypeEnum.LAST_24_HOURS) {
                return [this.getDailyDateFacet(field)];
            } else {
                return [this.getHourlyDateFacet(field)];
            }
        }else {
            if (dateQuery.relativeEndOffsetH - dateQuery.relativeStartOffsetH > 24) {
                // The date range covers more than 24 hours
                return [this.getDailyDateFacet(field)];
            } else {
                // The date range covers 24 hours or less
                return [this.getHourlyDateFacet(field)];
            }
        }
    }

    /**
     * Gets a DateFacetModel requesting hourly slices
     */
    private getHourlyDateFacet(field: DateFacetTypesEnum): DateFacetModel {
        return {
            field: field,
            prominenceGroupIds: !!this.prominenceTrendGroup ? [this.prominenceTrendGroup?.id] : [],
            sliceSizeInSecond: 3600 // 60 minutes * 60 seconds
        };
    }

    /**
     * Gets a DateFacetModel requesting daily slices
     */
    private getDailyDateFacet(field: DateFacetTypesEnum): DateFacetModel {
        return {
            field: field,
            prominenceGroupIds: !!this.prominenceTrendGroup ? [this.prominenceTrendGroup?.id] : [],
            sliceSizeInSecond: 86400 // 24 hours * 60 minutes * 60 seconds
        };
    }

    public hasValidSavedMentionSource(): boolean {
        return !!(this.mentions.folderId || this.mentions.tagId || this.mentions.tagGroupId || this.mentions.sourceGroupId || this.mentions.aiAgentId);
    }
    public hasValidMentionsSource(): boolean {
        return !!(this.mentions.topicId || this.mentions.folderId || this.mentions.tagId || this.mentions.tagGroupId || this.mentions.sourceGroupId
            || this.mentions.mediaSubscriptionId || this.mentions.aiAgentId);
    }
    public hasValidExternalSource(): boolean {
        return this.externalData && !!(this.externalData?.dataConnectorId && this.externalData?.externalDataId);
    }
    public hasValidSocialSource(): boolean {
        return !!(this.mentions.themeId || this.mentions.socialTopicId);
    }
    public hasValidGoogleAnalyticsSource(): boolean {
        return this.hasValidExternalSource() && this.externalData.externalDataType === DataConnectorTypesEnum.GOOGLE_ANALYTICS;
    }

    /**
     * Checks if the series is valid to be loaded
     */
    public isValid(): boolean {
        if (!(this.hasValidMentionsSource() || this.hasValidExternalSource() || this.hasValidSocialSource()) && !this.useMentionProvider) {
            return false;
        }
        let hasDateRange = false;

        if (this.dateRange.type === DateQueryTypeEnum.RELATIVE) {
            hasDateRange = !isNaN(this.dateRange.relativeEndOffsetH) && !isNaN(this.dateRange.relativeEndOffsetH);
        } else if (this.dateRange.type === DateQueryTypeEnum.ABSOLUTE) {
            hasDateRange = !!this.dateRange.absoluteDate ||
                !!(this.dateRange.absoluteRange && this.dateRange.absoluteRange.start && this.dateRange.absoluteRange.end);
        } else if (this.dateRange.type === DateQueryTypeEnum.RELATIVE_TIMEZONE) {
            hasDateRange = !!this.dateRange.relativeTimezoneDateType;
        }else if (this.dateRange.type === DateQueryTypeEnum.ALL) {
            hasDateRange = true;

            if (!this.canUseAllDateRange()) {
                return false;
            }
        }

        return hasDateRange;
    }

    public canUseAllDateRange(): boolean {
        const seriesTypesWithoutAll = [
            SeriesTypeEnum.topic,
            SeriesTypeEnum.subscription,
            SeriesTypeEnum.googleAnalytics,
            SeriesTypeEnum.theme,
            SeriesTypeEnum.socialTopic
        ];

        return !seriesTypesWithoutAll.includes(this.type);
    }

    public canUseLast24HoursDateRange(): boolean {
        // REFERRAL_TREND chart does not support last 24 hours
        return this.chartType !== ReportBlockTypeEnum.REFERRAL_TREND;
    }

    /**
     * update the series with global data
     */
    updateGlobalSource(series: ReportSeriesModel, passSearchCriteriaId: boolean = false): void {
        this.mentions.facetTypes = series.mentions.facetTypes;
        this.chartType = series.chartType;
        this.termType = series.termType;
        this.dateRange = series.dateRange;
        this.sentiments = series.sentiments || [];
        this.referralTypes = series.referralTypes;
        this.prEvents = series.prEvents;
        this.styles = series.styles;

        if (!this.mentions.filters) {
            this.mentions.filters = new MentionSearchModel();
        }
        if (passSearchCriteriaId) {
            // we want to keep the global setting filters, but assign it the series IDs
            this.mentions.searchCriteriaId = series.mentions.searchCriteriaId;
            if (series.mentions.filters) {
                this.mentions.filters.id = series.mentions.filters.id;
                this.mentions.filters.name = series.mentions.filters.name;
                this.mentions.filters.type = series.mentions.filters.type;
            } else {
                // name and id are paired in B/E, and should be kept or deleted together
                delete this.mentions.filters.id;
                delete this.mentions.filters.name;
            }
        } else {
            // this is just a fail safe and should not run..
            // passSearchCriteriaId should only be false if the global settings array of sources is less than the one in the chart (which should never happen)
            // but we would only want to delete the IDs, so we don't have multiple charts with the same search IDs
            delete this.mentions.searchCriteriaId;

            // name and id are paired in B/E, and should be kept or deleted together
            delete this.mentions.filters.id;
            delete this.mentions.filters.name;
        }

        this.id = null;
    }

    public getSeriesUrl(useCombinedQuery: boolean = true): string {
        if (this.hasValidMentionsSource()) {
            return this.getMentionSeriesUrl(undefined, undefined, useCombinedQuery);
        }
        if (this.hasValidSocialSource()) {
            return this.getMentionSeriesUrl(undefined, undefined, useCombinedQuery);
        }
        if (this.hasValidGoogleAnalyticsSource()) {
            return 'DataConnector';
        }
    }

    public getMentionSeriesUrl(dataPointFilters?: MentionSearchCriteriaModel, previousPeriod?: boolean, useCombinedQuery: boolean = true): string {
        if (!mentionSeriesTypes.includes(this.type) && !socialSeriesTypes.includes(this.type)) {
            return null;
        }

        let filters = this.mentions?.filters?.searchCriteria?.clone() ?? new MentionSearchCriteriaModel();
        const excludedMediaTypes = filters.query?.excludedMediaTypes;
        if (useCombinedQuery && this.mentions.combinedQuery) {
            filters.query = this.mentions.combinedQuery;
            if (filters.query.includedMediaTypes?.length === Object.keys(MediaTypeEnum).length && !filters.filterQuery?.includedMediaTypes?.length) {
                filters.query.includedMediaTypes = [];
            }

            if (excludedMediaTypes?.length) {
                filters.query.includedMediaTypes = [];
                filters.query.excludedMediaTypes = excludedMediaTypes;
            }

            if (filters.query.sentiments?.length === Object.keys(SearchCriteriaQuerySentimentsEnum).length && !filters.filterQuery?.sentiments?.length) {
                filters.query.sentiments = [];
            }

            filters.filterQuery = null;
        }
        let url = 'Monitoring/';

        switch (this.type) {
            case SeriesTypeEnum.topic:
                url += `topics/topic/${this.mentions.topicId}`;

                // use the chart's date range, not the topic's. @TODO check if they're the same first
                filters.query.dateQuery = DateQueryModel.clone(this.dateRange);
                break;

            case SeriesTypeEnum.subscription:
                url += `subscriptions/subscription/${this.mentions.mediaSubscriptionId}`;

                // use the chart's date range, not the sub's. @TODO check if they're the same first
                filters.query.dateQuery = DateQueryModel.clone(this.dateRange);
                break;

            case SeriesTypeEnum.folder:
                url += `mentions/folder/${this.mentions.folderId}`;

                if (this.dateRange.type === DateQueryTypeEnum.ALL) {
                    filters.query.dateQuery = DateQueryModel.getAllQuery();
                } else {
                    filters.query.dateQuery = DateQueryModel.clone(this.dateRange);
                }

                break;

            case SeriesTypeEnum.tag:
                url += 'mentions/saved';

                filters.query.tag.include = [ this.mentions.tagId ];
                filters.query.dateQuery = DateQueryModel.clone(this.dateRange);
                break;

            case SeriesTypeEnum.tagGroup:
                url += 'mentions/saved';

                filters.query.tagGroup.include = [ this.mentions.tagGroupId ];
                filters.query.dateQuery = DateQueryModel.clone(this.dateRange);
                break;
            case SeriesTypeEnum.sourceGroup:
                url += 'mentions/saved';

                filters.query.sourceGroup.include = [ this.mentions.sourceGroupId ];
                filters.query.dateQuery = DateQueryModel.clone(this.dateRange);
                break;
            case SeriesTypeEnum.aiAgent:
                // The agent's own feed is already scoped to it, so only the series' filters travel with it
                url = agentSectionRoute(this.mentions.aiAgentId, AgentRouteSectionEnum.mentions).substring(1);

                filters.query.dateQuery = DateQueryModel.clone(this.dateRange);
                break;
            case SeriesTypeEnum.theme:
                url = `Social/themes/theme/${this.mentions.themeId}/postFeed`;

                filters.query.theme.include = [ this.mentions.themeId ];

                if (this.styles?.socialTopicIds?.length) {
                    filters.query.agent.include = this.styles?.socialTopicIds;
                }
                filters.query.dateQuery = DateQueryModel.clone(this.dateRange);
                break;
            case SeriesTypeEnum.socialTopic:
                if (this.styles?.socialTopicThemeIds?.length) {
                    url = `Social/themes/theme/${this.styles?.socialTopicThemeIds[0]}/postFeed`;
                } else {
                    return null;
                }

                filters.query.agent.include = [ this.mentions.socialTopicId ];
                filters.query.dateQuery = DateQueryModel.clone(this.dateRange);
                break;
            default:
                // 'other', 'preview' or other placeholder segment - we don't want to do anything
                return null;
        }

        if (previousPeriod) {
            try {
                filters.query.dateQuery = DateQueryModel.getPreviousPeriod(DateQueryModel.makeAbsoluteRange(filters.query.dateQuery));
            } catch(error) {
                if (filters.query.dateQuery.type !== DateQueryTypeEnum.ALL) {
                    throw new Error(error);
                }
            }
        }

        if (dataPointFilters) {
            // fix Sentiment charts not loading when applying sentiment filters
            const validateSentiments = filters.query.sentiments.filter(sentiment => {
                return !!dataPointFilters.query.sentiments.find(sentimentFilter => sentiment.id === sentimentFilter.id);
            });

            if (validateSentiments.length <= 0 && filters.query.sentiments.length > 0 ) {
                dataPointFilters.query.sentiments = filters.query.sentiments;
            }

            filters = MentionSearchCriteriaModel.createFiltered(filters, dataPointFilters);
        }

        // only apply filters if the filter object has been modified from the default
        if (!filters.equals(new MentionSearchCriteriaModel())) {
            if (!this.seriesUniqueUuid) {
                this.seriesUniqueUuid = uuid.v4();
            }
            url += `?filters=${MentionSearchCriteriaModel.toFilterQueryString(filters, this.seriesUniqueUuid)}`;
        }

        return url;
    }

    public getSocialSeriesUrl(dataPointFilters?: MentionSearchCriteriaModel, previousPeriod?: boolean, combineFilters: boolean = false): string {
        if (!this.fullSearch) {
            return '';
        }

        const filterSearch = MentionSearchCriteriaModel.createFiltered(this.fullSearch, dataPointFilters, combineFilters, false);

        if (this.mentions.isPreviousPeriod || previousPeriod) {
            filterSearch.isPreviousPeriod = true;
        }

        if (dataPointFilters) {
            filterSearch.sortField = dataPointFilters.sortField;
        }

        if (!this.seriesUniqueUuid) {
            this.seriesUniqueUuid = uuid.v4();
        }
        const url = `/?filters=${MentionSearchCriteriaModel.toFilterQueryString(filterSearch, this.seriesUniqueUuid)}`;

        return url;
    }

    /**
     * clear data
     */
    clearData(): void {
        this.dataProvider.next(null);
    }

    resetSearchCriteriaId(): void {
        if (this.mentions) {
            this.mentions.searchCriteriaId = null;
            if (this.mentions.filters) {
                this.mentions.filters.id = null;
                this.mentions.filters.name = null;
            }
        }
    }

    private setMentionsIds(apiModel?: ReportSeriesApiModel | SharedReportSeriesApiModel) {
        if (apiModel) {
            switch (apiModel.sourceType) {
                case SeriesSourceTypesEnum.topic:
                    this.mentions.topicId = apiModel.sourceId ?? 0;
                    break;
                case SeriesSourceTypesEnum.folder:
                    this.mentions.folderId = apiModel.sourceId ?? 0;
                    break;
                case SeriesSourceTypesEnum.tag:
                    this.mentions.tagId = apiModel.sourceId ?? 0;
                    break;
                case SeriesSourceTypesEnum.tagGroup:
                    this.mentions.tagGroupId = apiModel.sourceId ?? 0;
                    break;
                case SeriesSourceTypesEnum.sourceGroup:
                    this.mentions.sourceGroupId = apiModel.sourceId ?? 0;
                    break;
                case SeriesSourceTypesEnum.subscription:
                    this.mentions.mediaSubscriptionId = apiModel.sourceId ?? 0;
                    break;
                case SeriesSourceTypesEnum.theme:
                    this.mentions.themeId = apiModel.sourceId ?? 0;
                    break;
                case SeriesSourceTypesEnum.agent:
                    this.mentions.socialTopicId = apiModel.sourceId ?? 0;
                    break;
                case SeriesSourceTypesEnum.aiAgent:
                    this.mentions.aiAgentId = apiModel.sourceId ?? 0;
                    break;
            }
        } else {
            this.mentions.topicId = 0;
            this.mentions.folderId = 0;
            this.mentions.tagId = 0;
            this.mentions.tagGroupId = 0;
            this.mentions.sourceGroupId = 0;
            this.mentions.mediaSubscriptionId = 0;
            this.mentions.themeId = 0;
            this.mentions.socialTopicId = 0;
            this.mentions.aiAgentId = 0;
        }
    }

    getSeriesSourceId(): number {
        return this.mentions.topicId || this.mentions.folderId || this.mentions.tagId || this.mentions.tagGroupId
            || this.mentions.sourceGroupId || this.mentions.mediaSubscriptionId || this.mentions.themeId
            || this.mentions.socialTopicId || this.mentions.aiAgentId || 0;
    }

    isSavedSource(): boolean {
        // if all saved coverage or a new search
        if (this.fullSearch) {
            return this.fullSearch.isAccountSearch || !!this.mentions.folderId || !!this.mentions.tagId || !!this.mentions.tagGroupId || !!this.mentions.sourceGroupId
                || !!this.mentions.aiAgentId;
        }

        // we cannot use folderId etc, because for all saved coverage case, all saved source ids will be empty
        // so if it is not unsaved source, then it is saved source
        return !this.isUnsavedSource();
    }

    isUnsavedSource(): boolean {
        return !!(this.mentions.topicId || this.mentions.mediaSubscriptionId || this.mentions.themeId || this.mentions.socialTopicId);
    }

    setNeoFacetsFromChartSettings(): void {
        switch (this._chartType) {
            case ReportBlockTypeEnum.GEOGRAPHY_BREAKDOWN_BY_VOLUME:
                if (this.styles.geographyScale === GeographyTypesEnum.country) {
                    this.mentions.neoFacets = [{
                        termField: NeoFacetTermFieldApiEnum.PROVINCE,
                        key: NeoFacetTermFieldApiEnum.PROVINCE,
                        aggregateBucketRequests: [
                            {
                                key: NeoFacetTermFieldApiEnum.AUDIENCE_REACH,
                                field: NeoFacetTermFieldApiEnum.AUDIENCE_REACH,
                                function: BucketFunctionApiEnum.INDIVIDUAL_SUM
                            },
                            {
                                key: NeoFacetTermFieldApiEnum.AD_VALUE_USD,
                                field: NeoFacetTermFieldApiEnum.AD_VALUE_USD,
                                function: BucketFunctionApiEnum.INDIVIDUAL_SUM
                            }
                        ]
                    }];
                } else {
                    this.mentions.neoFacets = [{
                        termField: NeoFacetTermFieldApiEnum.COUNTRY,
                        key: NeoFacetTermFieldApiEnum.COUNTRY,
                        aggregateBucketRequests: [
                            {
                                key: NeoFacetTermFieldApiEnum.AUDIENCE_REACH,
                                field: NeoFacetTermFieldApiEnum.AUDIENCE_REACH,
                                function: BucketFunctionApiEnum.INDIVIDUAL_SUM
                            },
                            {
                                key: NeoFacetTermFieldApiEnum.AD_VALUE_USD,
                                field: NeoFacetTermFieldApiEnum.AD_VALUE_USD,
                                function: BucketFunctionApiEnum.INDIVIDUAL_SUM
                            }
                        ]
                    }];
                }
                break;
            case ReportBlockTypeEnum.TOP_10_AUTHORS_BY_VOLUME:
            case ReportBlockTypeEnum.TOP_10_OUTLETS:
                const neoFacets = this.mentions.neoFacets = this._chartType === ReportBlockTypeEnum.TOP_10_AUTHORS_BY_VOLUME ?
                    JSON.parse(JSON.stringify(topContactsNeoFacetRequest)) : JSON.parse(JSON.stringify(topOutletsNeoFacetRequest));
                if (this.metric !== ChartMetricsEnum.volumeOfMentions && this.metric) {
                    neoFacets.termSort = {
                        aggregateBucketKey: chartMetricsToNeoFacetTermFieldMap.get(this.metric),
                        sortBy: NeoFacetSortByEnum.aggregate,
                        sortOrder: SortOrderEnum.descending
                    };
                }
                if (!this.styles.entityProfile) {
                    delete neoFacets.plainBucketRequests;
                }
                this.mentions.neoFacets = [neoFacets];
                break;
        }
    }

    public setChartType(type: ReportBlockTypeEnum): void {
        this._chartType = type;
    }
}
